import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Clock, 
  Users, 
  MessageCircle, 
  ChevronRight, 
  AlertCircle, 
  CheckCircle,
  ThumbsUp,
  Minus,
  ThumbsDown,
  Loader2,
  ArrowRight,
  Pause,
  Radio,
  Compass,
  Zap
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Timer component
const RoundTimer = ({ startTime, durationMinutes }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!startTime || !durationMinutes) return;

    const totalMs = durationMinutes * 60 * 1000;
    const start = new Date(startTime).getTime();

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - start;
      const remaining = Math.max(0, totalMs - elapsed);
      const pct = (remaining / totalMs) * 100;

      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
      setProgress(pct);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, durationMinutes]);

  return (
    <div className="text-center" data-testid="round-timer">
      <div className="text-5xl font-mono text-[#F5F0E6] tracking-wider mb-3 font-light">
        {timeLeft || '--:--'}
      </div>
      <div className="w-48 h-1 bg-[#222] mx-auto rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#8B1A1A] to-[#B82828] transition-all duration-1000 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// Broadcast overlay — admin message to all users
const BroadcastOverlay = ({ broadcast, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const dur = (broadcast.display_duration_seconds || 15) * 1000;
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(onDismiss, 600);
    }, dur);
    return () => clearTimeout(timer);
  }, [broadcast, onDismiss]);

  const typeConfig = {
    info: { icon: Radio, accent: '#C4A76B', label: 'Aviso del organizador' },
    dinamica: { icon: Compass, accent: '#8B1A1A', label: 'Nueva dinámica' },
    instruccion: { icon: Zap, accent: '#B82828', label: 'Instrucción' }
  };

  const cfg = typeConfig[broadcast.type] || typeConfig.info;
  const Icon = cfg.icon;

  return (
    <div
      className={`fixed inset-x-0 top-0 z-[60] flex justify-center pt-20 pointer-events-none transition-all duration-600
        ${visible && !exiting ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}
      data-testid="broadcast-overlay"
    >
      <div
        className="pointer-events-auto max-w-lg w-full mx-6 p-6 border backdrop-blur-xl rounded-sm"
        style={{
          background: `linear-gradient(135deg, rgba(11,11,11,0.95), rgba(30,10,10,0.92))`,
          borderColor: `${cfg.accent}40`,
          boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 40px ${cfg.accent}15`
        }}
        onClick={() => { setExiting(true); setTimeout(onDismiss, 600); }}
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ backgroundColor: `${cfg.accent}20` }}>
            <Icon size={20} style={{ color: cfg.accent }} />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: cfg.accent }}>
              {cfg.label}
            </p>
            <p className="text-[#F5F0E6] text-lg font-serif leading-relaxed">
              {broadcast.message}
            </p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 h-px bg-[#333] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              backgroundColor: cfg.accent,
              animation: `shrink ${broadcast.display_duration_seconds || 15}s linear forwards`
            }}
          />
        </div>
      </div>
      <style>{`
        @keyframes shrink { from { width: 100%; } to { width: 0%; } }
      `}</style>
    </div>
  );
};

// Transition overlay between rounds
const RoundTransition = ({ roundNumber, theme, guidance, onReady }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  return (
    <div className={`fixed inset-0 z-50 bg-[#0B0B0B] flex items-center justify-center transition-opacity duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}
         data-testid="round-transition">
      <div className="text-center max-w-lg px-8">
        {/* Animated ring */}
        <div className="relative w-32 h-32 mx-auto mb-10">
          <div className="absolute inset-0 border-2 border-[#8B1A1A]/30 rounded-full animate-ping" />
          <div className="absolute inset-2 border border-[#8B1A1A]/60 rounded-full animate-pulse" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl font-serif text-[#8B1A1A]">{roundNumber}</span>
          </div>
        </div>

        <p className="label-premium mb-4 tracking-[0.3em]">Nueva Interacción</p>
        <h2 className="text-3xl sm:text-4xl font-serif text-[#F5F0E6] mb-4">{theme}</h2>
        <p className="text-[#8A8A8A] text-lg mb-3 leading-relaxed">{guidance}</p>
        <p className="text-[#8B1A1A] text-sm uppercase tracking-widest mb-10">Cambia de mesa</p>

        <button
          onClick={onReady}
          className="btn-wine inline-flex items-center gap-3 px-12 py-4"
          data-testid="transition-ready-btn"
        >
          Estoy listo
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

// Micro-feedback form (blocking)
const FeedbackForm = ({ partnerName, roundNumber, onSubmit, loading }) => {
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');

  const options = [
    { value: 1, icon: ThumbsDown, label: 'No hubo conexión', color: '#666' },
    { value: 2, icon: Minus, label: 'Fue agradable', color: '#C4A76B' },
    { value: 3, icon: ThumbsUp, label: 'Buena conexión', color: '#8B1A1A' }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#0B0B0B]/95 backdrop-blur-md flex items-center justify-center px-6"
         data-testid="feedback-form-overlay">
      <div className="max-w-md w-full text-center">
        <p className="label-premium mb-3 tracking-[0.3em]">Ronda {roundNumber} finalizada</p>
        <h2 className="text-2xl sm:text-3xl font-serif text-[#F5F0E6] mb-2">
          ¿Cómo fue tu interacción?
        </h2>
        <p className="text-[#8A8A8A] mb-10">
          Con <span className="text-[#F5F0E6]">{partnerName}</span>
        </p>

        <div className="flex justify-center gap-6 mb-10">
          {options.map(opt => {
            const Icon = opt.icon;
            const isSelected = selected === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setSelected(opt.value)}
                className={`flex flex-col items-center gap-3 p-6 border rounded-lg transition-all duration-300 ${
                  isSelected 
                    ? 'border-[#8B1A1A] bg-[#8B1A1A]/15 scale-110 shadow-lg shadow-[#8B1A1A]/20' 
                    : 'border-[#333] bg-[#111] hover:border-[#555] hover:bg-[#1a1a1a]'
                }`}
                data-testid={`feedback-option-${opt.value}`}
              >
                <Icon size={32} style={{ color: isSelected ? opt.color : '#666' }} />
                <span className={`text-xs uppercase tracking-wider ${isSelected ? 'text-[#F5F0E6]' : 'text-[#8A8A8A]'}`}>
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Optional private note */}
        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Nota privada (opcional)"
          className="w-full bg-transparent border-b border-[#333] text-[#F5F0E6] py-3 mb-8 text-center text-sm focus:outline-none focus:border-[#8B1A1A] transition-colors placeholder:text-[#555]"
          data-testid="feedback-note-input"
        />

        <button
          onClick={() => selected && onSubmit(selected, note)}
          disabled={!selected || loading}
          className={`btn-wine w-full flex items-center justify-center gap-3 ${
            !selected ? 'opacity-40 cursor-not-allowed' : ''
          }`}
          data-testid="submit-feedback-btn"
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              Confirmar
              <CheckCircle size={18} />
            </>
          )}
        </button>

        <p className="text-[#555] text-xs mt-4 italic">
          Tu feedback es privado y nos ayuda a mejorar tus futuras conexiones
        </p>
      </div>
    </div>
  );
};

// Partner card — who you're meeting
const PartnerCard = ({ partner }) => {
  if (!partner) return null;

  return (
    <div className="card-premium p-8 max-w-sm mx-auto" data-testid="partner-card">
      {/* Avatar */}
      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#8B1A1A] to-[#5a1111] flex items-center justify-center mx-auto mb-6 text-3xl font-serif text-[#F5F0E6]">
        {partner.first_name?.[0] || '?'}
      </div>

      <h3 className="text-2xl font-serif text-[#F5F0E6] text-center mb-1">
        {partner.first_name}
      </h3>
      {partner.age && (
        <p className="text-center text-[#8A8A8A] text-sm mb-4">{partner.age} años</p>
      )}

      <div className="space-y-3 text-sm">
        {partner.occupation && (
          <div className="flex items-center gap-3 text-[#8A8A8A]">
            <div className="w-1 h-1 bg-[#8B1A1A] rounded-full" />
            {partner.occupation}
          </div>
        )}
        {partner.city && (
          <div className="flex items-center gap-3 text-[#8A8A8A]">
            <div className="w-1 h-1 bg-[#8B1A1A] rounded-full" />
            {partner.city}
          </div>
        )}
        {partner.bio_snippet && (
          <p className="text-[#666] text-xs italic mt-3 text-center leading-relaxed">
            "{partner.bio_snippet}"
          </p>
        )}
      </div>
    </div>
  );
};

// Waiting state between rounds
const WaitingState = ({ guidance, hint }) => (
  <div className="text-center py-16" data-testid="waiting-state">
    <div className="w-16 h-16 mx-auto mb-8 relative">
      <div className="absolute inset-0 border-2 border-[#8B1A1A]/40 rounded-full animate-pulse" />
      <Pause className="absolute inset-0 m-auto text-[#8B1A1A]" size={24} />
    </div>
    <p className="text-[#F5F0E6] text-lg font-serif mb-3">{guidance}</p>
    <p className="text-[#8A8A8A] text-sm">{hint}</p>
  </div>
);

// Main EventLiveMode component
const EventLiveMode = ({ eventId, onExit }) => {
  const { user } = useAuth();
  const [liveState, setLiveState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTransition, setShowTransition] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [lastRoundSeen, setLastRoundSeen] = useState(0);
  const [activeBroadcast, setActiveBroadcast] = useState(null);
  const [lastBroadcastId, setLastBroadcastId] = useState(null);
  const [microEmotionText, setMicroEmotionText] = useState(null);
  const pollRef = useRef(null);

  const fetchLiveState = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/live-state`, { withCredentials: true });
      const data = res.data;
      
      // BLOCKING: Force feedback before anything else
      if (data.pending_feedback && data.pending_feedback.length > 0 && !showFeedback) {
        const pf = data.pending_feedback[0];
        setFeedbackTarget(pf);
        setShowFeedback(true);
        setShowTransition(false); // Feedback takes priority over transition
      }
      // Only show transition if NO pending feedback
      else if (data.active_round && data.current_round_number > lastRoundSeen && lastRoundSeen > 0 && !showFeedback) {
        setShowTransition(true);
      }
      
      setLiveState(data);
      if (data.current_round_number > 0) {
        setLastRoundSeen(data.current_round_number);
      }
      
      // Detect new broadcast from admin
      if (data.broadcast && data.broadcast.broadcast_id !== lastBroadcastId) {
        setActiveBroadcast(data.broadcast);
        setLastBroadcastId(data.broadcast.broadcast_id);
      }
      
      setError(null);
    } catch (err) {
      console.error('Live state error:', err);
      if (err.response?.status === 403) {
        setError('No tienes acceso a este evento.');
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, lastRoundSeen, showFeedback]);

  // Poll every 4 seconds
  useEffect(() => {
    fetchLiveState();
    pollRef.current = setInterval(fetchLiveState, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchLiveState]);

  const handleFeedbackSubmit = async (rating, note) => {
    if (!feedbackTarget) return;
    setFeedbackLoading(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/interaction-feedback`, {
        round_id: feedbackTarget.round_id,
        assignment_id: feedbackTarget.assignment_id,
        connection_rating: rating,
        private_note: note
      }, { withCredentials: true });
      
      // Show micro-emotion if returned by backend (not always)
      const microEmotion = res.data?.micro_emotion;
      if (microEmotion) {
        setMicroEmotionText(microEmotion);
        setTimeout(() => setMicroEmotionText(null), 3000);
      }
      
      setShowFeedback(false);
      setFeedbackTarget(null);
      // Refresh state
      await fetchLiveState();
    } catch (err) {
      console.error('Feedback error:', err);
      alert(err.response?.data?.detail || 'Error al enviar feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleTransitionReady = () => {
    setShowTransition(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-40 bg-[#0B0B0B] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#8B1A1A] animate-spin mx-auto mb-4" />
          <p className="text-[#8A8A8A] text-sm">Conectando al evento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-40 bg-[#0B0B0B] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-[#8B1A1A] mx-auto mb-4" />
          <p className="text-[#F5F0E6]">{error}</p>
        </div>
      </div>
    );
  }

  if (!liveState || !liveState.live_active) {
    // Live mode ended
    if (liveState?.live_status === 'completed') {
      return (
        <div className="fixed inset-0 z-40 bg-[#0B0B0B] flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-8 border-2 border-[#8B1A1A]/40 rounded-full flex items-center justify-center">
              <CheckCircle className="text-[#8B1A1A]" size={32} />
            </div>
            <p className="label-premium mb-4 tracking-[0.3em]">Evento Finalizado</p>
            <h2 className="text-3xl font-serif text-[#F5F0E6] mb-4">Gracias por participar</h2>
            <p className="text-[#8A8A8A] mb-8 leading-relaxed">
              Pronto podrás seleccionar tus conexiones favoritas. Cada interacción que tuviste fue un paso hacia algo especial.
            </p>
            <button onClick={onExit} className="btn-wine inline-flex items-center gap-3 px-10 py-4" data-testid="exit-live-mode-btn">
              Continuar
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      );
    }
    return null;
  }

  const { active_round, my_assignment, partner_info, current_round_number, total_rounds } = liveState;
  const hasActiveRound = !!active_round;
  const hasAssignment = !!my_assignment;

  return (
    <div className="fixed inset-0 z-40 bg-[#0B0B0B] overflow-y-auto" data-testid="event-live-mode">
      {/* Round transition overlay */}
      {showTransition && active_round && (
        <RoundTransition
          roundNumber={current_round_number}
          theme={active_round.round_theme || 'Nueva Interacción'}
          guidance={active_round.guidance_message || ''}
          onReady={handleTransitionReady}
        />
      )}

      {/* Feedback overlay (blocking) */}
      {showFeedback && feedbackTarget && (
        <FeedbackForm
          partnerName={feedbackTarget.partner_name}
          roundNumber={feedbackTarget.round_number}
          onSubmit={handleFeedbackSubmit}
          loading={feedbackLoading}
        />
      )}

      {/* Broadcast overlay from admin */}
      {activeBroadcast && (
        <BroadcastOverlay
          broadcast={activeBroadcast}
          onDismiss={() => setActiveBroadcast(null)}
        />
      )}

      {/* Micro-emotion after feedback (subtle, fades out) */}
      {microEmotionText && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-30 px-6 py-3 bg-[#111] border border-[#8B1A1A]/20 backdrop-blur-md animate-pulse" data-testid="micro-emotion-toast">
          <p className="text-[#8A8A8A] text-sm italic text-center">{microEmotionText}</p>
        </div>
      )}

      {/* Header bar */}
      <div className="sticky top-0 z-30 bg-[#0B0B0B]/95 backdrop-blur-md border-b border-[#8B1A1A]/20 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <p className="label-premium text-xs tracking-[0.25em]">{liveState.event_title}</p>
            <p className="text-[#8A8A8A] text-xs mt-1">Evento en vivo</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-[#8A8A8A] text-xs">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span>En vivo</span>
            </div>
            <span className="text-[#8B1A1A] text-xs font-mono">
              {current_round_number}/{total_rounds}
            </span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        {hasActiveRound ? (
          <div className="space-y-10">
            {/* Round info */}
            <div className="text-center">
              <p className="label-premium mb-2 tracking-[0.3em]">
                Ronda {current_round_number} de {total_rounds}
              </p>
              <h1 className="text-3xl sm:text-4xl font-serif text-[#F5F0E6] mb-4">
                {active_round.round_theme}
              </h1>
              <p className="text-[#8A8A8A] max-w-md mx-auto leading-relaxed">
                {active_round.guidance_message}
              </p>
            </div>

            {/* Timer */}
            <RoundTimer 
              startTime={active_round.actual_start} 
              durationMinutes={active_round.duration_minutes || liveState.round_duration_minutes}
            />

            {/* Partner info */}
            {hasAssignment && partner_info ? (
              <div>
                <p className="text-center text-[#8A8A8A] text-sm mb-6 uppercase tracking-wider">Tu interacción</p>
                <PartnerCard partner={partner_info} />
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-[#555] mx-auto mb-4" />
                <p className="text-[#8A8A8A]">Esperando asignación...</p>
              </div>
            )}

            {/* Feedback status for current round */}
            {hasAssignment && liveState.has_given_active_feedback && (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-emerald-900/20 border border-emerald-500/30 px-4 py-2 rounded-lg">
                  <CheckCircle size={16} className="text-emerald-400" />
                  <span className="text-emerald-400 text-sm">Feedback enviado para esta ronda</span>
                </div>
              </div>
            )}

            {/* Interaction counter */}
            <div className="flex justify-center gap-8 text-center">
              <div>
                <p className="text-2xl font-serif text-[#F5F0E6]">{liveState.my_total_interactions}</p>
                <p className="text-[#8A8A8A] text-xs uppercase tracking-wider">Interacciones</p>
              </div>
              <div className="w-px bg-[#333]" />
              <div>
                <p className="text-2xl font-serif text-[#F5F0E6]">{current_round_number}</p>
                <p className="text-[#8A8A8A] text-xs uppercase tracking-wider">Ronda actual</p>
              </div>
            </div>
          </div>
        ) : (
          /* No active round — waiting */
          <WaitingState 
            guidance={liveState.current_guidance || 'Esperando la siguiente ronda...'}
            hint={liveState.next_action_hint || 'El administrador iniciará la ronda pronto'}
          />
        )}
      </div>
    </div>
  );
};

export default EventLiveMode;
